import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Shield, FileText, Download } from 'lucide-react';
import { Logo } from '../ui';

type Tab = 'privacy' | 'terms';
type Lang = 'es' | 'en';

const PDF_URLS: Record<Tab, Record<Lang, string>> = {
  privacy: {
    es: 'https://sistema.bsports.cl/descargas/PRIVACITY_ESP.pdf',
    en: 'https://sistema.bsports.cl/descargas/PRIVACITY_ENG.pdf',
  },
  terms: {
    es: 'https://sistema.bsports.cl/descargas/TOS_PAY_ESP.pdf',
    en: 'https://sistema.bsports.cl/descargas/TOS_PAY_ENG.pdf',
  },
};

/* ─── Privacy Policy content ─────────────────────────────────────────────── */

const PRIVACY_ES = `
<h1>POLÍTICA DE PRIVACIDAD APP BTALENTSTAR</h1>
<p>Por favor lea atentamente la siguiente Política de Privacidad relativa al uso de la App de Btalentstar, antes de usarla.</p>
<p>La presente Política de Privacidad regula el tratamiento de los Datos Personales del Usuario de la App, independientemente de su ubicación. Además, se aplican las normas adicionales o diferentes indicadas en la cláusula 7. Esta Política de Privacidad se aplica únicamente al tratamiento de Datos Personales a través de la App, y no se aplica a otros sitios web a los que se puede acceder por medio de enlaces desde la App.</p>
<p>El responsable del tratamiento de Datos Personales es:</p>
<p><strong>BSports USA LLC</strong> para Usuarios de Estados Unidos y México, EIN 88-3623514, ubicado en 3411 SILVERSIDE ROAD TATNALL BUILDING # 104 WILMINGTON, DE 19810. El Usuario puede contactar a BSports USA llamando al número +569943690000 o escribiendo un correo electrónico a <a href="mailto:contact@btalentstar.com">contact@btalentstar.com</a>.</p>
<p><strong>BSports SpA</strong>, Rol Único Tributario Nº 76.852.547-1, ubicado en La Capitanía N° 662, comuna de Las Condes, Santiago, para Usuarios de otros Países de Sudamérica. El Usuario puede contactar a BSports SpA llamando al número +56994369000 o escribiendo un correo electrónico a <a href="mailto:contacto@bsports.cl">contacto@bsports.cl</a>.</p>
<p>Los menores de edad u otras personas sin capacidad legal para contratar sólo pueden utilizar la App si sus representantes legales han dado su consentimiento para ello y para el tratamiento de Datos Personales descrito en esta Política de Privacidad.</p>

<h2>1. DEFINICIONES</h2>
<ul>
  <li><strong>App</strong>: la aplicación móvil "Btalentstar" que puede descargarse de las tiendas de aplicaciones.</li>
  <li><strong>BSports</strong>: el responsable del tratamiento de Datos Personales, como se especifica en el preámbulo.</li>
  <li><strong>Club profesional de fútbol</strong>: un club de fútbol que coopera con BSports y está registrado en la App.</li>
  <li><strong>Cuenta</strong>: la cuenta de un Usuario en la App.</li>
  <li><strong>Dato(s) Personal(es)</strong>: toda información relativa al Usuario, incluyendo su nombre, correo electrónico, número de cédula de identidad, y otros datos relativos a personas naturales, identificadas o identificables.</li>
  <li><strong>Política de Privacidad</strong>: las presentes normas relativas al tratamiento de Datos Personales del Usuario de la App por parte de BSports.</li>
  <li><strong>Tratamiento de Datos Personales</strong>: cualquier operación que permita recolectar, almacenar, grabar, organizar, elaborar, seleccionar, extraer, confrontar, interconectar, disociar, comunicar, ceder, transferir, transmitir o cancelar Datos Personales, o utilizarlos en cualquier otra forma.</li>
  <li><strong>Usuario</strong>: toda persona que use la App, representado por su representante legal, en su caso.</li>
</ul>

<h2>2. DATOS PERSONALES TRATADOS Y PROPÓSITO DE TRATAMIENTO</h2>
<h3>a. Datos técnicos</h3>
<p>Cada vez que se abre y utiliza la App, los servidores almacenan temporalmente datos técnicos del dispositivo del Usuario, como la dirección IP, fecha y hora de visita, cantidad de datos transferidos, identificador del dispositivo, resolución de pantalla, tipo de navegador y sistema operativo.</p>
<h3>b. Datos obtenidos al crear una Cuenta y subir videos</h3>
<p>Al crear una Cuenta se tratan datos de contacto (nombre, dirección, teléfono, correo electrónico), fecha de nacimiento, sexo, país de residencia, nacionalidad, experiencia futbolística, posición en cancha, pie hábil, peso, estatura y contraseña. Cuando el Usuario sube videos, su contenido es procesado para almacenamiento y acceso por parte de los Clubes profesionales.</p>
<h3>c. Datos tratados al recibir newsletters y contactar BSports</h3>
<p>BSports utiliza los datos de contacto del Usuario para enviarle información sobre sus servicios. El Usuario puede solicitar no seguir siendo contactado en cualquier momento.</p>
<h3>d. Cookies y tecnologías similares</h3>
<p>La App utiliza cookies funcionales, analíticas y de marketing para optimizar la experiencia del Usuario y mejorar el servicio.</p>

<h2>3. TRANSFERENCIA DE DATOS PERSONALES</h2>
<p>BSports otorga acceso a los Clubes profesionales de fútbol a los Datos Personales contenidos en la Cuenta del Usuario y a los videos, excepto la contraseña. BSports puede transferir Datos Personales a terceros proveedores de servicios tecnológicos y, en caso de solicitud de autoridad o tribunal competente, a dichas entidades.</p>

<h2>4. DERECHOS DEL USUARIO</h2>
<p>Los Usuarios tienen derechos respecto a sus Datos Personales conforme a las leyes de protección de datos de su país, descritos en la sección 7.</p>

<h2>5. PLAZO DE CONSERVACIÓN DE DATOS</h2>
<p>Los Datos Personales se eliminarán cuando dejen de ser necesarios para el fin para el que se recogieron, en especial si el Usuario elimina su Cuenta, salvo que existan plazos legales de conservación.</p>

<h2>6. MODIFICACIONES Y CONTACTO</h2>
<p>Cualquier modificación a esta Política de Privacidad será notificada oportunamente al Usuario. Si el Usuario desea ejercer sus derechos o tiene preguntas, puede contactar a BSports a través de los datos indicados al inicio de este documento.</p>

<h2>7. NORMAS ADICIONALES O DIFERENTES PARA DETERMINADOS PAÍSES</h2>
<h3>a. Chile</h3>
<p>Se comprobará la validez del RUT del Usuario. Los Usuarios en Chile tienen derecho a exigir información, rectificación, eliminación, bloqueo de datos personales, oponerse a su uso comercial y revocar el consentimiento en cualquier momento.</p>
<h3>b. Argentina</h3>
<p>Se comprobará la validez del DNI. Los Usuarios en Argentina tienen los mismos derechos descritos para Chile.</p>
<h3>c. Colombia</h3>
<p>Se comprobará la validez de la cédula de ciudadanía. Los Usuarios en Colombia tienen los mismos derechos descritos para Chile.</p>
<h3>d. Perú</h3>
<p>Se comprobará la validez del DNI. Los Usuarios en Perú tienen los mismos derechos descritos para Chile.</p>
<h3>e. Estados Unidos</h3>
<p>Se comprobará la validez del Passport Card. Esta Política se rige por la ley del Estado de Delaware. Los servidores están ubicados en Brasil. Los Usuarios de EE.UU. tienen derecho a exigir información, rectificación, eliminación, bloqueo y revocar el consentimiento en cualquier momento.</p>

<p class="version">Versión 10-01-2023</p>
`;

const PRIVACY_EN = `
<h1>PRIVACY POLICY BTALENTSTAR APP</h1>
<p>Please read the following Privacy Policy regarding the use of the Btalentstar App carefully before using it.</p>
<p>This Privacy Policy governs the processing of Btalentstar App Users' Personal Data, regardless of their location. In addition, the additional or different regulations indicated in section 7 apply for the countries indicated. This Privacy Policy applies only to the processing of Personal Data through the App, and does not apply to other websites that can be accessed through links from the App.</p>
<p>The controller for the processing of Personal Data is:</p>
<p><strong>Bsports USA LLC</strong>, EIN 88-3623514, located at 3411 SILVERSIDE ROAD TATNALL BUILDING # 104 WILMINGTON, DE 19810, for Users in the United States and México. Users may contact Bsports USA by writing an email to <a href="mailto:contact@btalentstar.com">contact@btalentstar.com</a>.</p>
<p><strong>Bsports SpA</strong>, tax number 76.852.547-1, located in La Capitanía N° 662, commune of Las Condes, Santiago, for Users from other countries of South America. Users may contact Bsports SpA by writing an email to <a href="mailto:contacto@bsports.cl">contacto@bsports.cl</a>.</p>
<p>Minors or other persons without legal capacity to contract may only use the Btalentstar App if their legal representatives have given their consent to do so and to the processing of Personal Data described in this Privacy Policy.</p>

<h2>1. DEFINITIONS</h2>
<ul>
  <li><strong>Account</strong>: the account of a User in the App.</li>
  <li><strong>App</strong>: the mobile application Btalentstar that can be downloaded from the app stores.</li>
  <li><strong>Personal Data</strong>: all information relating to a User, including name, email address, identity card number, and other data concerning natural, identified or identifiable persons.</li>
  <li><strong>Privacy Policy</strong>: the present regulations regarding the processing of Personal Data of App Users by Bsports.</li>
  <li><strong>Processing of Personal Data</strong>: any operation that makes it possible to collect, store, record, organize, elaborate, select, extract, compare, interconnect, dissociate, communicate, assign, transfer, transmit or cancel Personal Data, or use it in any other way.</li>
  <li><strong>Professional Soccer Club</strong>: a soccer club that cooperates with Bsports and is registered in the App.</li>
  <li><strong>User</strong>: any person using the App, represented by his or her legal representative, if applicable.</li>
</ul>

<h2>2. PERSONAL DATA PROCESSED AND PURPOSE OF PROCESSING</h2>
<h3>a. Technical data</h3>
<p>Each time the App is opened and used, the servers temporarily store technical data from Users' devices, including IP address, date and time of visit, amount of data transferred, device identifier, screen resolution, browser type and operating system.</p>
<h3>b. Data obtained by creating an Account and uploading videos</h3>
<p>When Users create an Account, their contact details (name, address, telephone number, email address), date of birth, gender, country of residence, nationality, experience as a soccer player, position on the court, skilful foot, weight, height and password are processed. When Users store videos in the App, their content is processed to ensure recording, storage and access by Professional Soccer Clubs.</p>
<h3>c. Data processed when receiving newsletters and contacting Bsports</h3>
<p>Bsports uses Users' contact details to send them information about its services. Users may request not to be contacted for these purposes at any time.</p>
<h3>d. Cookies and similar technologies</h3>
<p>The App uses functional, analytics and marketing cookies to optimize Users' experience and improve the service.</p>

<h2>3. TRANSFER OF PERSONAL DATA</h2>
<p>Bsports grants Professional Soccer Clubs access to the Personal Data contained in Users' Accounts and to videos, except passwords. Bsports may transfer Personal Data to third-party technology service providers and, upon request from a competent authority or court, to such entities.</p>

<h2>4. USER RIGHTS</h2>
<p>Users have certain rights with respect to their Personal Data, depending on the countries in which they live or the applicable data protection laws, as described in section 7.</p>

<h2>5. DATA RETENTION PERIOD</h2>
<p>Personal Data will be deleted as soon as they are no longer necessary to achieve the purpose for which they were collected, particularly if Users delete their Account, unless legal retention periods apply.</p>

<h2>6. MODIFICATIONS AND CONTACT</h2>
<p>Any modification to this Privacy Policy will be duly notified to the Users. If Users wish to assert their rights or have any questions, they may contact Bsports through the contact details listed at the beginning of this document.</p>

<h2>7. ADDITIONAL OR DIFFERENT REGULATIONS FOR CERTAIN COUNTRIES</h2>
<h3>a. United States of America</h3>
<p>The validity of the Passport Card will be checked. This Privacy Policy is governed exclusively by the Law of the State of Delaware. Servers are located in Brazil. Competent courts are in Delaware. Users have the right to demand information, amendment, deletion, blocking of Personal Data, object to commercial use, and revoke consent at any time.</p>
<h3>b. Chile</h3>
<p>The validity of the RUT will be checked. Users have the right to demand information, amendment, deletion, blocking of Personal Data, object to commercial use, and revoke consent at any time.</p>
<h3>c. Argentina</h3>
<p>The validity of the DNI will be checked. Users have the same rights described for Chile.</p>
<h3>d. Colombia</h3>
<p>The validity of the CC (Citizenship Card) will be checked. Users have the same rights described for Chile.</p>
<h3>e. Perú</h3>
<p>The validity of the DNI will be checked. Users have the same rights described for Chile.</p>

<p class="version">Version 10-01-2023</p>
`;

/* ─── Terms & Conditions content ─────────────────────────────────────────── */

const TERMS_ES = `
<h1>TÉRMINOS Y CONDICIONES DE USO APP BTALENTSTAR</h1>
<p>Por favor lea atentamente los siguientes Términos y Condiciones de la App Btalentstar, antes de usarla.</p>
<p>Los presentes Términos y Condiciones rigen el uso que todo Usuario hace de la App de BTalentstar, independientemente de su ubicación. Se reservan todos los derechos y permisos no otorgados en forma expresa en los presentes Términos y Condiciones.</p>
<p>Los menores de edad sólo pueden utilizar la App si sus representantes legales han dado su consentimiento y supervisan su uso. Por medio de la aprobación de los presentes Términos y Condiciones, se entiende que el Usuario los ha leído y aceptado en todas sus partes.</p>

<h2>1. DEFINICIONES</h2>
<ul>
  <li><strong>App</strong>: la aplicación móvil "BTalentstar".</li>
  <li><strong>BSports</strong>: el proveedor de la App, como se especifica en la cláusula 2.</li>
  <li><strong>Club profesional de fútbol</strong>: un club de fútbol que coopera con BSports y está registrado en la App.</li>
  <li><strong>Contenido del Usuario</strong>: todos los datos proporcionados por el Usuario en la App, incluidos los vídeos.</li>
  <li><strong>Cuenta</strong>: la cuenta de un Usuario en la App.</li>
  <li><strong>Propiedad Intelectual</strong>: derechos de autor, logotipos, marcas registradas, nombres comerciales, eslóganes y otros indicios de origen asociados a BSports.</li>
  <li><strong>Usuario</strong>: toda persona que use la App, representado por su representante legal, en su caso.</li>
</ul>

<h2>2. INFORMACIÓN SOBRE EL PROVEEDOR Y COMUNICACIONES</h2>
<p><strong>BSports USA LLC</strong>, EIN 88-3623514, 3411 SILVERSIDE ROAD TATNALL BUILDING # 104 WILMINGTON, DE 19810. Contacto: <a href="mailto:contact@btalentstar.com">contact@btalentstar.com</a></p>
<p><strong>BSports SpA</strong>, RUT Nº 76.852.547-1, La Capitanía N° 662, Las Condes, Santiago. Contacto: <a href="mailto:contacto@bsports.cl">contacto@bsports.cl</a></p>

<h2>3. SOBRE LA APP</h2>
<p>La App Btalentstar es una aplicación móvil que permite a jóvenes futbolistas de entre 8 y 23 años realizar sesiones de entrenamiento profesional y tener un perfil deportivo digital para presentarse a Clubes profesionales de fútbol.</p>
<p>Los Usuarios deben crear una Cuenta y seleccionar un plan:</p>
<ul>
  <li><strong>Plan Freemium</strong>: gratuito, sin duración determinada. Permite explorar la App y realizar entrenamientos gratuitos, pero no postular a clubes profesionales.</li>
  <li><strong>Plan 3 Clubes profesionales de fútbol</strong>: permite postular a 3 clubes de forma sucesiva durante 2 años.</li>
  <li><strong>Plan 6 Clubes profesionales de fútbol</strong>: permite postular a 6 clubes de forma sucesiva durante 2 años.</li>
  <li><strong>Plan 10 Clubes profesionales de fútbol</strong>: permite postular a 10 clubes de forma sucesiva durante 2 años.</li>
</ul>
<p>Los planes de pago tienen duración de dos años y no se renuevan automáticamente. Los Clubes deben responder en un plazo de 30 días con una de las siguientes opciones: "Liberado", "Seleccionado" o "Inscrito".</p>

<h2>4. CUENTAS DE USUARIOS</h2>
<p>El Usuario es responsable de la exactitud de la información y de todas las actividades de su Cuenta. BSports se reserva el derecho de cerrar la Cuenta de un Usuario si éste incumple los Términos y Condiciones.</p>

<h2>5. PAGO</h2>
<p>Los planes se pagan con los medios de pago admitidos por Stripe, Mercado Pago u otro proveedor disponible en la App. BSports no es responsable de interrupciones o demoras de los sistemas de pago de terceros.</p>

<h2>6. DERECHOS DEL USUARIO</h2>
<p>El Usuario tendrá todos los derechos que le correspondan según la legislación aplicable. Las devoluciones se realizarán por el mismo medio de pago original.</p>

<h2>7. USO PERMITIDO DE LA APP</h2>
<p>El Usuario garantiza que no usará la App para propósitos ilícitos. En particular, se compromete a no proporcionar antecedentes falsos, no utilizar sistemas automáticos, no reproducir ni modificar la App, no hackear o descompilar la App, y no realizar actividades fraudulentas.</p>

<h2>8. LIMITACIÓN DE RESPONSABILIDAD</h2>
<p>BSports sólo ofrece la posibilidad de que los Usuarios se presenten a los Clubes profesionales. No es responsable de las acciones de los Clubes, de los costos de traslado o alojamiento para pruebas presenciales, ni de daños fuera de su control razonable.</p>

<h2>9. CONTACTO CON EL USUARIO</h2>
<p>El Usuario acepta que la App, los Clubes y sus socios comerciales lo contacten por correo electrónico, SMS, WhatsApp u otros medios para informarle sobre ofertas y novedades. El Usuario puede solicitar no seguir siendo contactado en cualquier momento.</p>

<h2>10. ENLACES A SITIOS WEB DE TERCEROS</h2>
<p>La App puede contener enlaces a sitios web de terceros. BSports no controla ni es responsable del contenido de dichos sitios.</p>

<h2>11. MODIFICACIONES</h2>
<p>Cualquier modificación a estos Términos y Condiciones será notificada oportunamente al Usuario, quien deberá manifestar su aceptación para continuar usando la App.</p>

<h2>12. NORMAS ADICIONALES O DIFERENTES PARA DETERMINADOS PAÍSES</h2>
<h3>a. Chile</h3>
<p>Aplica la Ley 19.496 de protección de los derechos de los consumidores. BSports excluye el derecho a retracto de 10 días.</p>
<p>Precios: Plan 3 Clubes: CLP 49.000 / Plan 6 Clubes: CLP 69.000 / Plan 10 Clubes: CLP 99.000 (por 2 años).</p>
<h3>b. Argentina</h3>
<p>Aplica la Ley 20.240. BSports excluye el derecho a retracto de 10 días.</p>
<p>Precios: Plan 3 Clubes: USD 49 / Plan 6 Clubes: USD 69 / Plan 10 Clubes: USD 99 (por 2 años).</p>
<h3>c. Colombia</h3>
<p>Aplica la Ley 1.480. BSports excluye el derecho a retracto de 10 días.</p>
<p>Precios: Plan 3 Clubes: USD 49 / Plan 6 Clubes: USD 69 / Plan 10 Clubes: USD 99 (por 2 años).</p>
<h3>d. Perú</h3>
<p>Aplica la Constitución Política del Perú, Artículo 110. BSports excluye el derecho a retracto de 10 días.</p>
<p>Precios: Plan 3 Clubes: USD 49 / Plan 6 Clubes: USD 69 / Plan 10 Clubes: USD 99 (por 2 años).</p>
`;

const TERMS_EN = `
<h1>TERMS AND CONDITIONS BTALENTSTAR MEMBERSHIP PLANS</h1>
<p>Please read the following Terms and Conditions of the Btalentstar App carefully before using it.</p>
<p>These Terms and Conditions govern all Users' use of the Btalentstar App, regardless of their location. All rights and permissions not expressly granted in these Terms and Conditions are reserved.</p>
<p>Minors or other persons without legal capacity to contract may only use the App if their legal representatives have given their consent and supervise the use of the App. By approving these Terms and Conditions, Users acknowledge they have read and accepted them in all their parts.</p>

<h2>1. DEFINITIONS</h2>
<ul>
  <li><strong>Account</strong>: the account of a User in the App.</li>
  <li><strong>App</strong>: the mobile application "Btalentstar".</li>
  <li><strong>BSports</strong>: the App provider, as specified in clause 2.</li>
  <li><strong>Intellectual Property</strong>: copyrights, logos, trademarks, trade names, slogans and other indications of origin associated with Btalentstar.</li>
  <li><strong>Professional Soccer Club</strong>: a soccer club that cooperates with Btalentstar and is registered in the App.</li>
  <li><strong>User</strong>: any person using the App, represented by his or her legal representative, if applicable.</li>
  <li><strong>User Content</strong>: all data provided by Users in the App, including videos.</li>
</ul>

<h2>2. PROVIDER INFORMATION AND COMMUNICATIONS</h2>
<p><strong>BSports USA LLC</strong>, EIN 88-3623514, 3411 SILVERSIDE ROAD TATNALL BUILDING # 104 WILMINGTON, DE 19810. Contact: <a href="mailto:contact@btalentstar.com">contact@btalentstar.com</a></p>
<p><strong>BSports SpA</strong>, tax number 76.852.547-1, La Capitanía N° 662, Las Condes, Santiago. Contact: <a href="mailto:contacto@bsports.cl">contacto@bsports.cl</a></p>

<h2>3. ABOUT THE APP</h2>
<p>The App is a mobile application that allows young soccer players aged 8 to 23 to conduct professional soccer training sessions and build a digital sports profile to present themselves to Professional Soccer Clubs.</p>
<p>Users must create an Account and select a plan:</p>
<ul>
  <li><strong>Freemium Plan</strong>: free of charge, no fixed duration. Allows exploring the App and free training sessions, but does not allow applying to clubs.</li>
  <li><strong>Plan 3 Professional Soccer Clubs</strong>: consecutive applications to 3 clubs over 2 years.</li>
  <li><strong>Plan 6 Professional Soccer Clubs</strong>: consecutive applications to 6 clubs over 2 years.</li>
  <li><strong>Plan 10 Professional Soccer Clubs</strong>: consecutive applications to 10 clubs over 2 years.</li>
</ul>
<p>Paid plans are valid for two years and do not automatically renew. Clubs must respond within 30 calendar days: "Released", "Selected" or "Enrolled".</p>

<h2>4. USER ACCOUNT</h2>
<p>Users are responsible for the accuracy of information entered in the App and for all activities of their Account. BSports reserves the right to close a User's Account if the User breaches these Terms and Conditions.</p>

<h2>5. PAYMENT</h2>
<p>Plans may only be paid with the means of payment supported by Stripe, Mercado Pago or other payment providers available in the App. BSports is not responsible for interruptions or delays in third-party payment systems.</p>

<h2>6. USER RIGHTS</h2>
<p>Users have all the rights to which they are entitled under applicable law. Refunds will be made by the same means of payment originally used.</p>

<h2>7. PERMITTED USE OF THE APP</h2>
<p>Users warrant they will not use the App for unlawful purposes and agree not to: provide false information; use automated systems; reproduce, modify or exploit the App; hack or reverse-engineer the App; or engage in any fraudulent activity.</p>

<h2>8. LIMITATION OF RESPONSIBILITY</h2>
<p>BSports only provides the opportunity for Users to introduce themselves to Professional Soccer Clubs. BSports is not responsible for the actions of the Clubs, travel or accommodation costs for live auditions, or damages beyond its reasonable control.</p>

<h2>9. CONTACT WITH USERS</h2>
<p>Users agree that BSports, the Professional Soccer Clubs or their partners may contact them by email, SMS, WhatsApp or other means to provide information about offers and news. Users may opt out at any time.</p>

<h2>10. LINKS TO THIRD PARTY WEBSITES</h2>
<p>The App may contain links to third-party websites. BSports does not control and is not responsible for the content of such websites.</p>

<h2>11. MODIFICATIONS</h2>
<p>Any modification to these Terms and Conditions will be duly notified to the Users, who must express their acceptance to continue using the App.</p>

<h2>12. ADDITIONAL OR DIFFERENT REGULATIONS FOR CERTAIN COUNTRIES</h2>
<h3>a. Chile</h3>
<p>Law 19.496 on Consumer Rights applies. BSports expressly excludes the right of withdrawal within 10 days.</p>
<p>Prices: Plan 3 Clubs: CLP 49,000 / Plan 6 Clubs: CLP 69,000 / Plan 10 Clubs: CLP 99,000 (for 2 years).</p>
<h3>b. Argentina</h3>
<p>Law 20.240 on Consumer Rights applies. BSports expressly excludes the right of withdrawal within 10 days.</p>
<p>Prices: Plan 3 Clubs: USD 49 / Plan 6 Clubs: USD 69 / Plan 10 Clubs: USD 99 (for 2 years).</p>
<h3>c. Colombia</h3>
<p>Law 1.480 on Consumer Rights applies. BSports expressly excludes the right of withdrawal within 10 days.</p>
<p>Prices: Plan 3 Clubs: USD 49 / Plan 6 Clubs: USD 69 / Plan 10 Clubs: USD 99 (for 2 years).</p>
<h3>d. Perú</h3>
<p>Political Constitution of Perú, Article 110 applies. BSports expressly excludes the right of withdrawal within 10 days.</p>
<p>Prices: Plan 3 Clubs: USD 49 / Plan 6 Clubs: USD 69 / Plan 10 Clubs: USD 99 (for 2 years).</p>
`;

const CONTENT: Record<Tab, Record<Lang, string>> = {
  privacy: { es: PRIVACY_ES, en: PRIVACY_EN },
  terms: { es: TERMS_ES, en: TERMS_EN },
};

/* ─── Component ──────────────────────────────────────────────────────────── */

export function Policies() {
  const { t, i18n } = useTranslation();
  const lang: Lang = i18n.language?.startsWith('es') ? 'es' : 'en';

  const searchTab = new URLSearchParams(window.location.search).get('tab') as Tab | null;
  const [activeTab, setActiveTab] = useState<Tab>(
    searchTab === 'terms' ? 'terms' : 'privacy',
  );

  const tabs: { id: Tab; labelKey: string; Icon: typeof Shield }[] = [
    { id: 'privacy', labelKey: 'policies.tabs.privacy', Icon: Shield },
    { id: 'terms', labelKey: 'policies.tabs.terms', Icon: FileText },
  ];

  return (
    <div className="min-h-screen bg-brand-darkNavy text-white flex flex-col">
      {/* Top bar */}
      <header className="border-b border-brand-lightBlue/20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between">
          <a href="/" aria-label="BTalentStar"><Logo /></a>
          <a href="/" className="inline-flex items-center gap-2 text-sm text-dark-400 hover:text-brand-yellow transition-colors">
            <ArrowLeft className="w-4 h-4" />
            {t('policies.back')}
          </a>
        </div>
      </header>

      <main className="flex-1 container mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
          <h1 className="text-2xl md:text-3xl font-bold mb-2">{t('policies.title')}</h1>
          <p className="text-dark-400 mb-8">{t('policies.subtitle')}</p>

          {/* Tabs + download */}
          <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
            <div className="flex gap-2">
              {tabs.map(({ id, labelKey, Icon }) => (
                <button
                  key={id}
                  onClick={() => setActiveTab(id)}
                  className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 border ${
                    activeTab === id
                      ? 'bg-brand-yellow text-brand-darkNavy border-brand-yellow shadow-lg shadow-brand-yellow/25'
                      : 'bg-transparent text-dark-400 border-brand-lightBlue/30 hover:border-brand-yellow/50 hover:text-white'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {t(labelKey)}
                </button>
              ))}
            </div>
            <a
              href={PDF_URLS[activeTab][lang]}
              download
              className="inline-flex items-center gap-1.5 text-xs text-dark-400 hover:text-brand-yellow transition-colors border border-brand-lightBlue/30 hover:border-brand-yellow/50 rounded-full px-3 py-2"
            >
              <Download className="w-3.5 h-3.5" />
              {t('policies.download')}
            </a>
          </div>

          {/* Content */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab + lang}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
              className="bg-brand-navy/60 backdrop-blur-xl border border-brand-lightBlue/20 rounded-2xl p-6 md:p-10 prose-legal"
              dangerouslySetInnerHTML={{ __html: CONTENT[activeTab][lang] }}
            />
          </AnimatePresence>
        </motion.div>
      </main>

      <div className="border-t border-brand-lightBlue/20 py-5">
        <p className="text-center text-xs text-dark-500">
          © {new Date().getFullYear()} BTalentStar.{' '}
          <a href="/eliminar-data" className="hover:text-brand-yellow transition-colors">
            {t('footer.deleteAccount')}
          </a>
        </p>
      </div>
    </div>
  );
}

export default Policies;
